import { useState, useMemo } from 'react';
import { LookupValueEditor } from './LookupValueEditor';
import {
  getGroupedCollections,
  getLookupCollectionConfig,
  LOOKUP_COLLECTIONS_CONFIG,
  type LookupCollectionConfig,
  type LookupGroup,
} from '../../config/lookupCollectionsConfig';
import { useLookupCollectionProperties, type CollectionPropertiesMap } from '../../hooks/useLookupCollectionProperties';

export function LookupCollectionList() {
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const groupedCollections = useMemo(() => getGroupedCollections(), []);

  // Get all collection names to fetch live properties
  const allCollectionNames = useMemo(
    () => LOOKUP_COLLECTIONS_CONFIG.map((c) => c.collectionName),
    []
  );
  const { propertiesMap, loading: propertiesLoading } = useLookupCollectionProperties(allCollectionNames);

  const formatCollectionName = (name: string): string => {
    return name
      .replace('lookup_', '')
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Filter collections based on search query
  const filteredGroupedCollections = useMemo(() => {
    if (!searchQuery.trim()) {
      return groupedCollections;
    }
    const term = searchQuery.toLowerCase();
    return groupedCollections
      .map(({ group, collections }) => ({
        group,
        collections: collections.filter(
          (c) =>
            c.displayName.toLowerCase().includes(term) ||
            c.collectionName.toLowerCase().includes(term) ||
            c.description.toLowerCase().includes(term)
        ),
      }))
      .filter(({ collections }) => collections.length > 0);
  }, [searchQuery, groupedCollections]);

  // Get total count of collections
  const totalCollections = LOOKUP_COLLECTIONS_CONFIG.length;
  const filteredCount = filteredGroupedCollections.reduce(
    (acc, { collections }) => acc + collections.length,
    0
  );

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const expandAll = () => setCollapsedGroups(new Set());
  const collapseAll = () =>
    setCollapsedGroups(new Set(groupedCollections.map((g) => g.group.id)));

  if (selectedCollection) {
    const config = getLookupCollectionConfig(selectedCollection);
    return (
      <LookupValueEditor
        collectionName={selectedCollection}
        displayName={config?.displayName || formatCollectionName(selectedCollection)}
        onBack={() => setSelectedCollection(null)}
      />
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Lookup Collections</h2>
        <p className="text-sm text-gray-500 mt-1">
          {searchQuery
            ? `Showing ${filteredCount} of ${totalCollections} lookup collections`
            : `Manage ${totalCollections} lookup collections organized into ${groupedCollections.length} groups`}
        </p>
      </div>

      {/* Search and Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search collections by name or description..."
          className="w-full sm:flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        />
        {!searchQuery && (
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="text-sm text-brand-600 hover:text-brand-700 px-3 py-2"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="text-sm text-brand-600 hover:text-brand-700 px-3 py-2"
            >
              Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Grouped Collections */}
      <div className="space-y-4">
        {filteredGroupedCollections.map(({ group, collections }) => (
          <GroupSection
            key={group.id}
            group={group}
            collections={collections}
            isCollapsed={collapsedGroups.has(group.id)}
            onToggle={() => toggleGroup(group.id)}
            onSelectCollection={setSelectedCollection}
            propertiesMap={propertiesMap}
            propertiesLoading={propertiesLoading}
          />
        ))}
      </div>

      {filteredGroupedCollections.length === 0 && searchQuery && (
        <p className="text-center text-gray-500 py-8">
          No collections found matching "{searchQuery}"
        </p>
      )}
    </div>
  );
}

interface GroupSectionProps {
  group: LookupGroup;
  collections: LookupCollectionConfig[];
  isCollapsed: boolean;
  onToggle: () => void;
  onSelectCollection: (name: string) => void;
  propertiesMap: CollectionPropertiesMap;
  propertiesLoading: boolean;
}

function GroupSection({
  group,
  collections,
  isCollapsed,
  onToggle,
  onSelectCollection,
  propertiesMap,
  propertiesLoading,
}: GroupSectionProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Group Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${
              isCollapsed ? '' : 'rotate-90'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
          <h3 className="font-medium text-gray-900">{group.displayName}</h3>
          <span className="text-sm text-gray-500">
            ({collections.length} {collections.length === 1 ? 'collection' : 'collections'})
          </span>
        </div>
      </button>

      {/* Collections Grid */}
      {!isCollapsed && (
        <div className="p-4 border-t border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {collections.map((collection) => (
              <CollectionCard
                key={collection.collectionName}
                collection={collection}
                onClick={() => onSelectCollection(collection.collectionName)}
                liveProperties={propertiesMap[collection.collectionName]}
                propertiesLoading={propertiesLoading}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface CollectionCardProps {
  collection: LookupCollectionConfig;
  onClick: () => void;
  liveProperties?: {
    isMultiSelect: boolean;
    isActive: boolean;
    description?: string;
  };
  propertiesLoading: boolean;
}

function CollectionCard({ collection, onClick, liveProperties, propertiesLoading }: CollectionCardProps) {
  // Use live Firebase value if available, otherwise fall back to config
  const isMultiSelect = liveProperties?.isMultiSelect ?? collection.isMultiSelect;

  return (
    <button
      onClick={onClick}
      className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-left hover:border-brand-500 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h4 className="font-medium text-gray-900 group-hover:text-brand-600 text-sm truncate">
            {collection.displayName}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
            {collection.description}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs font-mono text-gray-400 truncate">
              {collection.collectionName}
            </span>
            {propertiesLoading ? (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-400">
                ...
              </span>
            ) : isMultiSelect ? (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                Multi
              </span>
            ) : null}
          </div>
        </div>
        <svg
          className="w-4 h-4 text-gray-400 group-hover:text-brand-500 flex-shrink-0 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </div>
    </button>
  );
}
